/**
 * THE LAST FEW WINNERS OF THIS ROOM, drawn on the screen that waits for the next round.
 *
 * **It is a SLOT, and that is the whole of why it lives here rather than under `game/`.** Nothing
 * in `apps/web/src/game` may know tRPC exists — the rule `ChampionCallout` and `RunStanding` are
 * already under — so the arena takes a `ReactNode` and this is what `routes/play.tsx`, the one
 * file that knows about both, fills it with. Every test of the HUD stays a render with no
 * provider anywhere near it.
 *
 * **It is a query rather than something the room told us**, because the room cannot: a room is an
 * in-memory object the reaper closes a minute after the last human leaves, and this board is read
 * at exactly the moment somebody has walked into a quiet one. See
 * `services/rooms/round-winners.service.ts`.
 */

import { RECENT_WINNERS_LIMIT } from '@chompdome/shared/constants';
import { BotGlyph } from '@/game/hud/glyphs';
import { trpc } from '@/lib/trpc';

export interface RecentWinnersProps {
  /** The room being waited in. Absent before the first `welcome`, which draws nothing. */
  roomId: string | null;
  /**
   * The round that has just finished, straight off the `match` frame — see `pending`.
   *
   * Absent everywhere but the ceremony, which is the one moment the query cannot answer for.
   */
  current?: RecentWinnerRow | null;
  /**
   * The wrapper's layout, so one component can be the block under a ceremony and the column
   * beside a death panel. Everything inside it is unchanged, which is the point: a second drawing
   * of this board would drift away from the one players read between rounds.
   */
  className?: string;
}

/** One line of the board, whether it came from the database or off the match frame. */
export interface RecentWinnerRow {
  round: number;
  name: string;
  isBot: boolean;
  mass: number;
}

/**
 * The board, with the round that has just finished at the top of it.
 *
 * **THE QUERY CANNOT ANSWER FOR THE CURRENT ROUND YET, AND THAT IS WHY THIS EXISTS.** A round's
 * winner reaches `room_round_winners` by way of the worker draining `takeRoundWinners`, batching
 * it and POSTing it to `/internal/matches` — so for the first seconds of the very ceremony this
 * board is drawn under, the newest row in the database is the round *before* this one. Andy: "the
 * last 5 won for deathmatch should include the current match winner."
 *
 * It is prepended from the `match` frame rather than fetched again, which is what makes it
 * instant and what makes it need no wire change: `MatchView.standings` is the round's own
 * scoreboard and its first row **is** the winner, with the mass they won on.
 *
 * **DE-DUPLICATED BY ROUND, NAME AND MASS — AND NOT BY THE ROUND ALONE, WHICH WAS A BUG.** The
 * query will catch up (a refetch during a long ceremony, or the next lobby remounting this) and
 * the same round arriving from both sources must not be two lines. The round number looks like
 * the key and is not one: a room's counter restarts at 1 every time the reaper closes the room
 * and its declaration wakes again, so a fetched row from an earlier lifetime can carry the same
 * number as the round that has just been played and would silently suppress it — which is the
 * client half of exactly the mistake `room_round_winners` was keyed on. All three together are
 * the same win for any purpose this board has: a player who wins twice running on the same mass
 * to the point is a coincidence worth one line.
 */
export function withCurrentRound(
  rows: readonly RecentWinnerRow[],
  current: RecentWinnerRow | null | undefined,
  limit: number,
): readonly RecentWinnerRow[] {
  if (current == null) return rows;
  if (
    rows.some(
      (row) =>
        row.round === current.round && row.name === current.name && row.mass === current.mass,
    )
  ) {
    return rows;
  }
  return [current, ...rows].slice(0, limit);
}

export function RecentWinners({
  roomId,
  current,
  className = 'mt-6 w-full max-w-xs',
}: RecentWinnersProps) {
  /**
   * Fetched per room rather than polled.
   *
   * The board only changes when a round ends, and a round ending is followed by a whole
   * intermission of ceremony — so the moment it goes stale is the moment the next lobby remounts
   * this anyway. A poller would be a request every few seconds for a list that changes twice a
   * minute.
   */
  const winners = trpc.rooms.recentWinners.useQuery(
    { roomId: roomId ?? '' },
    { enabled: roomId !== null, staleTime: 15_000 },
  );

  const fetched = winners.data ?? [];
  // Capped at what the server would have returned, so prepending the live round pushes the oldest
  // line off rather than growing the board by one during every ceremony.
  const rows = withCurrentRound(fetched, current, Math.max(fetched.length, RECENT_WINNERS_LIMIT));
  // A room that has never finished a round draws nothing at all — the ordinary state of a freshly
  // declared one, and better than a heading over an empty list.
  if (rows.length === 0) return null;

  return (
    <div data-testid="recent-winners" className={`pointer-events-none text-left ${className}`}>
      <p className="text-center text-[10px] font-black uppercase tracking-[0.25em] text-muted-foreground">
        Recent winners
      </p>
      <ol className="mt-2 space-y-1">
        {rows.map((row) => (
          <li
            // The round number is not unique across a room's lifetimes — see `withCurrentRound`.
            key={`${row.round}:${row.name}:${row.mass}`}
            className="flex items-center gap-2 text-xs tabular-nums text-muted-foreground"
          >
            {/* The mark a bot's blob already wears, so a name that is software reads as software
                rather than as somebody who keeps winning here. */}
            {row.isBot && <BotGlyph className="h-3 w-3 shrink-0" />}
            <span className="min-w-0 flex-1 truncate font-semibold text-foreground">
              {row.name}
            </span>
            <span>{row.mass.toLocaleString('en-US')}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}
